import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter,
  type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { templatesApiService } from '../services/templatesApiService'
import { foldersApiService, type AffectedApiKey, type ApprovalMoveImpact, type FolderNotEmptyDetail } from '../services/foldersApiService'
import { ApiError } from '../services/BaseApiService'
import type { Folder, Template } from '../types'
import { colors, fontFamily, fontSize, radius, shadow, spacing, tracking, transition } from '../styles/theme'
import { Button, StatusChip, Badge, Modal, ConfirmModal, Input } from '../components/ui'
import ImportTemplatesModal from '../components/ImportTemplatesModal'
import FolderTree, { useExpandedFolders, type DragPayload } from '../components/FolderTree'
import MoveToFolderModal from '../components/MoveToFolderModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileCirclePlus, faUpload, faFolderPlus, faFolder, faFileLines,
  faPen, faTrash, faArrowRightArrowLeft, faChevronRight, faUserCheck, faTableCellsLarge,
} from '@fortawesome/free-solid-svg-icons'
import { templateStatusLabel } from '../utils/templateStatus'
import { usePublishBreadcrumb } from '../contexts/BreadcrumbContext'
import { useIsLiveWorkspace } from '../contexts/AppStateContext'
import LiveReadOnlyBanner from '../components/templates/LiveReadOnlyBanner'
import { APPROVAL_LEVEL_NAME, FolderPromotionApprovalModal } from '../components/templates/PromotionApproval'
import { useIsTeamAdmin } from '../hooks/useCurrentUser'
import { TemplateStatus } from '../constants'
import {
  buildFolderTree, ancestorChain, isSelfOrDescendant,
  pathDepth, subtreeMaxDepth, MAX_FOLDER_DEPTH, type FolderNode,
} from '../utils/folderTree'

// ── Contents-pane rows ───────────────────────────────────────────────────────

const cellStyle: React.CSSProperties = { padding: `${spacing.sm} ${spacing.md}` }

function ScopedKeyList({ intro, keys }: { intro: string; keys: AffectedApiKey[] }) {
  return (
    <div style={{ marginTop: spacing.md }}>
      <p style={{ margin: 0 }}>{intro}</p>
      <ul style={{ margin: `${spacing.sm} 0 0`, paddingLeft: spacing.lg }}>
        {keys.map(k => (
          <li key={k.apiKeyId}>
            <strong>{k.name}</strong> ({k.isTest ? 'Test' : 'Live'}, {k.keyPrefix}…) scoped to {k.scopeFolderName ?? 'a folder'}
          </li>
        ))}
      </ul>
    </div>
  )
}

const APPROVAL_CHANGES_SHOWN = 5

function ApprovalChangeList({ impact }: { impact: ApprovalMoveImpact }) {
  const more = impact.count - Math.min(impact.changes.length, APPROVAL_CHANGES_SHOWN)
  return (
    <div style={{ marginTop: spacing.md }} data-testid="approval-move-changes">
      <p style={{ margin: 0 }}>Promotion approval changes for {impact.count} template{impact.count === 1 ? '' : 's'}:</p>
      <ul style={{ margin: `${spacing.sm} 0 0`, paddingLeft: spacing.lg }}>
        {impact.changes.slice(0, APPROVAL_CHANGES_SHOWN).map(c => (
          <li key={c.templateId}>
            <strong>{c.name}</strong> — {APPROVAL_LEVEL_NAME[c.from]} → {APPROVAL_LEVEL_NAME[c.to]}
          </li>
        ))}
      </ul>
      {more > 0 && <p style={{ margin: `${spacing.xs} 0 0` }}>and {more} more</p>}
    </div>
  )
}

function FolderContentRow({ folder, onOpen, onRename, onDelete, onMove, onApproval, draggingFolderPath, readOnly = false }: {
  folder: Folder
  readOnly?: boolean
  /** Team admins only. */
  onApproval?: (folder: Folder) => void
  onOpen: (folderId: string) => void
  onRename: (folder: Folder) => void
  onDelete: (folder: Folder) => void
  onMove: (folder: Folder) => void
  draggingFolderPath?: string
}) {
  const [hovered, setHovered] = useState(false)
  const forbidden = !!draggingFolderPath && folder.path.startsWith(draggingFolderPath)

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `row-folder-${folder.folderId}`,
    data: { folderId: folder.folderId, path: folder.path },
    disabled: forbidden,
  })
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `drag-row-folder-${folder.folderId}`,
    data: { kind: 'folder', id: folder.folderId, name: folder.name, path: folder.path } satisfies DragPayload,
  })

  const active = isOver && !forbidden

  return (
    <tr
      ref={setDropRef}
      data-testid="folder-row"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderBottom: `1px solid ${colors.border}`,
        background: active ? colors.primaryLight : hovered ? colors.surfaceMist : 'transparent',
        boxShadow: active ? `inset 0 0 0 1px ${colors.primary}` : 'none',
        opacity: isDragging ? 0.4 : 1,
        transition: `background ${transition.fast}`,
      }}
    >
      <td style={cellStyle}>
        <div
          ref={setDragRef}
          {...listeners}
          {...attributes}
          style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}
        >
          <FontAwesomeIcon icon={faFolder} style={{ color: colors.textMuted, fontSize: 13 }} />
          <button
            onClick={() => onOpen(folder.folderId)}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: colors.primary, fontSize: fontSize.base, fontWeight: 500, cursor: 'pointer',
            }}
          >
            {folder.name}
          </button>
          {folder.promotionApproval != null && (
            <span title={`Promotion approval: ${APPROVAL_LEVEL_NAME[folder.promotionApproval]} (set on this folder)`}>
              <Badge color={colors.textSecondary} bg={colors.background} border={colors.border}>
                {APPROVAL_LEVEL_NAME[folder.promotionApproval]}
              </Badge>
            </span>
          )}
        </div>
      </td>
      <td style={cellStyle} />
      <td style={{ ...cellStyle, fontSize: fontSize.base, color: colors.textMuted }}>Folder</td>
      <td style={{ ...cellStyle, fontSize: fontSize.sm, color: colors.textSecondary }}>
        {new Date(folder.updatedAt || folder.createdAt).toLocaleDateString()}
      </td>
      <td style={cellStyle}>
        <RowActions
          visible={hovered && !readOnly}
          actions={readOnly ? [] : [
            { icon: faPen, label: 'Rename', onClick: () => onRename(folder) },
            { icon: faArrowRightArrowLeft, label: 'Move to…', onClick: () => onMove(folder) },
            ...(onApproval ? [{ icon: faUserCheck, label: 'Promotion approval…', onClick: () => onApproval(folder) }] : []),
            { icon: faTrash, label: 'Delete', onClick: () => onDelete(folder), danger: true },
          ]}
        />
      </td>
    </tr>
  )
}

function TemplateContentRow({ template, onOpen, onRename, onMove, readOnly = false }: {
  template: Template
  readOnly?: boolean
  onOpen: (templateId: string) => void
  onRename: (template: Template) => void
  onMove: (template: Template) => void
}) {
  const [hovered, setHovered] = useState(false)
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `drag-template-${template.templateId}`,
    data: { kind: 'template', id: template.templateId, name: template.name } satisfies DragPayload,
  })

  return (
    <tr
      data-testid="template-row"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderBottom: `1px solid ${colors.border}`,
        background: hovered ? colors.surfaceMist : 'transparent',
        opacity: isDragging ? 0.4 : 1,
        transition: `background ${transition.fast}`,
      }}
    >
      <td style={cellStyle}>
        <div
          ref={setNodeRef}
          {...listeners}
          {...attributes}
          style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}
        >
          <FontAwesomeIcon icon={faFileLines} style={{ color: colors.textMuted, fontSize: 13 }} />
          <button
            onClick={() => onOpen(template.templateId)}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: colors.primary, fontSize: fontSize.base, fontWeight: 500, cursor: 'pointer',
            }}
          >
            {template.name}
          </button>
        </div>
      </td>
      <td style={cellStyle}>
        {template.liveVersionId || template.templateStatusLookupId === TemplateStatus.ACTIVE
          ? <Badge color={colors.success} bg={colors.successLight} border={colors.successBorder}>Live</Badge>
          : <StatusChip status={templateStatusLabel(template.templateStatusLookupId)} />}
      </td>
      <td style={{ ...cellStyle, fontSize: fontSize.base, color: colors.textSecondary }}>
        {template.description || '—'}
      </td>
      <td style={{ ...cellStyle, fontSize: fontSize.sm, color: colors.textSecondary }}>
        {new Date(template.updatedAt).toLocaleDateString()}
      </td>
      <td style={cellStyle}>
        <RowActions
          visible={hovered && !readOnly}
          actions={readOnly ? [] : [
            { icon: faPen, label: 'Rename', onClick: () => onRename(template) },
            { icon: faArrowRightArrowLeft, label: 'Move to…', onClick: () => onMove(template) },
          ]}
        />
      </td>
    </tr>
  )
}

function RowActions({ visible, actions }: {
  visible: boolean
  actions: { icon: typeof faPen; label: string; onClick: () => void; danger?: boolean }[]
}) {
  return (
    <div style={{ display: 'flex', gap: spacing.xs, justifyContent: 'flex-end', visibility: visible ? 'visible' : 'hidden' }}>
      {actions.map(a => (
        <button
          key={a.label}
          onClick={a.onClick}
          title={a.label}
          aria-label={a.label}
          style={{
            width: 26, height: 26,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: `1px solid ${a.danger ? colors.errorBorder : colors.border}`,
            borderRadius: radius.sm, cursor: 'pointer',
            color: a.danger ? colors.error : colors.textSecondary, fontSize: 11,
          }}
        >
          <FontAwesomeIcon icon={a.icon} />
        </button>
      ))}
    </div>
  )
}

/** Breadcrumb segments double as drop targets, so dragging up a level is one short drag. */
function BreadcrumbCrumb({ folderId, label, isLast, onClick, disabled }: {
  folderId: string | null
  label: string
  isLast: boolean
  onClick: () => void
  disabled: boolean
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `crumb-${folderId ?? 'root'}`,
    data: { folderId },
    disabled,
  })

  return (
    <button
      ref={setNodeRef}
      onClick={onClick}
      style={{
        background: isOver && !disabled ? colors.primaryLight : 'none',
        border: 'none',
        borderRadius: radius.sm,
        padding: '2px 6px',
        cursor: 'pointer',
        fontSize: fontSize.sm,
        fontWeight: isLast ? 500 : 400,
        color: isLast ? colors.text : colors.textSecondary,
        boxShadow: isOver && !disabled ? `inset 0 0 0 1px ${colors.primary}` : 'none',
      }}
    >
      {label}
    </button>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

type MoveTarget =
  | { kind: 'template'; template: Template }
  | { kind: 'folder'; folder: Folder }

export default function TemplatesPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const folderId = searchParams.get('folderId')

  const [approvalTarget, setApprovalTarget] = useState<Folder | null>(null)
  const isTeamAdmin = useIsTeamAdmin()
  const [folders, setFolders] = useState<Folder[]>([])
  // Every template in the workspace, not just the open folder's: the tree shows per-folder file
  // counts and leaf rows, so it needs the whole set. The contents pane filters this client-side.
  const [allTemplates, setAllTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const [showUpload, setShowUpload] = useState(false)
  const [newFolderOpen, setNewFolderOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [renaming, setRenaming] = useState<Folder | null>(null)
  const [renamingTemplate, setRenamingTemplate] = useState<Template | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null)
  const [deleting, setDeleting] = useState<{ folder: Folder; counts: FolderNotEmptyDetail | null; revokedKeys: AffectedApiKey[] } | null>(null)
  // A move that would cut a folder-scoped API key off from templates, or change their promotion approval: confirmed before it runs.
  const [scopeWarning, setScopeWarning] = useState<{ itemName: string; keys: AffectedApiKey[]; approval: ApprovalMoveImpact | null; run: () => Promise<void> } | null>(null)
  const [dragging, setDragging] = useState<DragPayload | null>(null)

  const { expanded, toggle, expandAll } = useExpandedFolders()
  // Live is read-only: no create/upload/rename/move/delete, and no drag sensors at all.
  const readOnly = useIsLiveWorkspace()

  const editSensors = useSensors(
    // 5px activation distance so clicking a row link doesn't start a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  )
  const noSensors = useSensors()
  const sensors = readOnly ? noSensors : editSensors

  // Not keyed on folderId — switching folders is a client-side filter, not a refetch.
  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [folderList, templateList] = await Promise.all([
        foldersApiService.listAll(),
        templatesApiService.listEverything(),
      ])
      setFolders(folderList)
      setAllTemplates(templateList)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const tree = useMemo(() => buildFolderTree(folders), [folders])
  const nodesById = useMemo(() => {
    const map = new Map<string, FolderNode>()
    const walk = (list: FolderNode[]) => list.forEach(n => { map.set(n.folder.folderId, n); walk(n.children) })
    walk(tree)
    return map
  }, [tree])

  const templatesByFolder = useMemo(() => {
    const map = new Map<string | null, Template[]>()
    for (const t of allTemplates) {
      const key = t.folderId ?? null
      const bucket = map.get(key)
      if (bucket) bucket.push(t)
      else map.set(key, [t])
    }
    for (const bucket of map.values()) bucket.sort((a, b) => a.name.localeCompare(b.name))
    return map
  }, [allTemplates])

  const templates = useMemo(() => templatesByFolder.get(folderId) ?? [], [templatesByFolder, folderId])

  const currentFolder = folderId ? folders.find(f => f.folderId === folderId) ?? null : null
  const crumbs = useMemo(() => ancestorChain(folders, folderId), [folders, folderId])

  // Mirror the open folder into the top-bar breadcrumb: Templates › folder1 › sub2.
  usePublishBreadcrumb(useMemo(() => [
    { label: 'Templates', to: '/app/templates' },
    ...crumbs.map(c => ({ label: c.name, to: `/app/templates?folderId=${c.folderId}` })),
  ], [crumbs]))
  const subfolders = useMemo(
    () => folders.filter(f => (f.parentFolderId ?? null) === folderId).sort((a, b) => a.name.localeCompare(b.name)),
    [folders, folderId],
  )

  function selectFolder(id: string | null) {
    setNotice('')
    setSearchParams(id ? { folderId: id } : {})
  }

  // Keeps the tree in sync with the newly-relevant branch after a create or move.
  function revealFolder(target: Folder | undefined) {
    if (!target) return
    const ancestors = target.path.split('/').filter(Boolean).filter(id => id !== target.folderId)
    if (ancestors.length) expandAll(ancestors)
  }

  // ── Drag and drop ──────────────────────────────────────────────────────────

  function handleDragStart(e: DragStartEvent) {
    setDragging(e.active.data.current as DragPayload)
  }

  /**
   * Runs a move now, or first asks for confirmation when it would take templates out of a folder-scoped
   * API key's folder (folders aren't versioned, so that key would start getting 403s in Live at once)
   * or would change the promotion approval level of the templates it moves.
   */
  async function withScopeCheck(
    itemName: string,
    change: { templateIds?: string[]; folderId?: string; targetFolderId: string | null },
    run: () => Promise<void>,
  ) {
    // Both checks are advisory; if one fails, the move still goes ahead as it always did.
    const [keys, approval] = await Promise.all([
      foldersApiService.scopeImpact(change).then(r => r.affectedKeys, () => [] as AffectedApiKey[]),
      foldersApiService.approvalMoveImpact(change).then(r => r, () => null),
    ])
    const approvalChanges = approval && approval.count > 0 ? approval : null
    if (keys.length > 0 || approvalChanges) {
      setScopeWarning({ itemName, keys, approval: approvalChanges, run })
      return
    }
    await run()
  }

  async function confirmScopeWarning() {
    if (!scopeWarning) return
    setBusy(true)
    setError('')
    try {
      await scopeWarning.run()
      setScopeWarning(null)
      setMoveTarget(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Move failed')
      setScopeWarning(null)
    } finally {
      setBusy(false)
    }
  }

  async function handleDragEnd(e: DragEndEvent) {
    const payload = e.active.data.current as DragPayload | undefined
    setDragging(null)
    if (readOnly || !payload || !e.over) return

    const targetFolderId = (e.over.data.current as { folderId: string | null } | undefined)?.folderId ?? null
    setError('')
    setNotice('')

    try {
      if (payload.kind === 'template') {
        const template = allTemplates.find(t => t.templateId === payload.id)
        if ((template?.folderId ?? null) === targetFolderId) return

        await withScopeCheck(payload.name, { templateIds: [payload.id], targetFolderId }, async () => {
          const result = await templatesApiService.moveBulk([payload.id], targetFolderId)
          if (result.moved === 0) {
            setError(`"${payload.name}" could not be moved.`)
            return
          }
          setNotice(`Moved "${payload.name}".`)
          await load()
        })
        return
      } else {
        const source = folders.find(f => f.folderId === payload.id)
        if (!source) return
        if ((source.parentFolderId ?? null) === targetFolderId) return
        // Belt-and-braces: the drop target is already disabled for the dragged folder's subtree.
        const target = targetFolderId ? folders.find(f => f.folderId === targetFolderId) : null
        if (target && isSelfOrDescendant(target.path, source.path)) return

        await withScopeCheck(payload.name, { folderId: payload.id, targetFolderId }, async () => {
          await foldersApiService.move(payload.id, targetFolderId)
          setNotice(`Moved "${payload.name}".`)
          if (target) expandAll([...target.path.split('/').filter(Boolean)])
          await load()
        })
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Move failed')
    }
  }

  // ── Folder CRUD ────────────────────────────────────────────────────────────

  const atDepthLimit = pathDepth(currentFolder?.path) >= MAX_FOLDER_DEPTH

  async function createFolder() {
    if (!newFolderName.trim()) return
    setBusy(true)
    setError('')
    try {
      const { folderId: created } = await foldersApiService.save({
        name: newFolderName.trim(),
        parentFolderId: folderId ?? undefined,
      })
      setNewFolderOpen(false)
      setNewFolderName('')
      const refreshed = await foldersApiService.listAll()
      setFolders(refreshed)
      revealFolder(refreshed.find(f => f.folderId === created))
      if (folderId) expandAll([folderId])
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create folder')
    } finally {
      setBusy(false)
    }
  }

  async function renameFolder() {
    if (!renaming || !renameValue.trim()) return
    setBusy(true)
    setError('')
    try {
      await foldersApiService.save({
        folderId: renaming.folderId,
        name: renameValue.trim(),
        parentFolderId: renaming.parentFolderId ?? undefined,
      })
      setRenaming(null)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to rename folder')
    } finally {
      setBusy(false)
    }
  }

  async function renameTemplate() {
    const name = renameValue.trim()
    if (!renamingTemplate || !name) return
    setBusy(true)
    setError('')
    try {
      await templatesApiService.update(renamingTemplate, name)
      setRenamingTemplate(null)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to rename template')
    } finally {
      setBusy(false)
    }
  }

  /** Tries the plain delete first; a 409 FOLDER_NOT_EMPTY escalates to the recursive confirm. */
  async function requestDelete(folder: Folder) {
    setError('')
    try {
      await foldersApiService.delete(folder.folderId)
      if (folderId === folder.folderId) selectFolder(folder.parentFolderId)
      await load()
    } catch (err) {
      if (err instanceof ApiError && err.code === 'FOLDER_NOT_EMPTY') {
        let revokedKeys: AffectedApiKey[] = []
        try {
          revokedKeys = (await foldersApiService.scopeImpact({ folderId: folder.folderId, del: true })).affectedKeys
        } catch {
          // Advisory only; the confirm still shows the folder contents.
        }
        setDeleting({ folder, counts: err.detail as FolderNotEmptyDetail, revokedKeys })
        return
      }
      setError(err instanceof ApiError ? err.message : 'Failed to delete folder')
    }
  }

  async function confirmRecursiveDelete() {
    if (!deleting) return
    setBusy(true)
    setError('')
    try {
      const result = await foldersApiService.deleteRecursive(deleting.folder.folderId)
      setNotice(
        `Deleted ${result.foldersDeleted} folder${result.foldersDeleted === 1 ? '' : 's'}` +
        (result.templatesReassigned > 0
          ? `; ${result.templatesReassigned} template${result.templatesReassigned === 1 ? '' : 's'} moved up one level.`
          : '.'),
      )
      if (folderId && isSelfOrDescendant(currentFolder?.path ?? '', deleting.folder.path)) {
        selectFolder(deleting.folder.parentFolderId)
      }
      setDeleting(null)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete folder')
    } finally {
      setBusy(false)
    }
  }

  async function performMove(targetFolderId: string | null) {
    if (!moveTarget) return
    setBusy(true)
    setError('')
    try {
      const target = moveTarget
      if (target.kind === 'template') {
        await withScopeCheck(target.template.name, { templateIds: [target.template.templateId], targetFolderId }, async () => {
          const result = await templatesApiService.moveBulk([target.template.templateId], targetFolderId)
          if (result.moved === 0) {
            setError(`"${target.template.name}" could not be moved.`)
            return
          }
          setMoveTarget(null)
          await load()
        })
      } else {
        await withScopeCheck(target.folder.name, { folderId: target.folder.folderId, targetFolderId }, async () => {
          await foldersApiService.move(target.folder.folderId, targetFolderId)
          setMoveTarget(null)
          await load()
        })
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Move failed')
    } finally {
      setBusy(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const thStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    textAlign: 'left',
    fontFamily: fontFamily.mono,
    fontSize: fontSize.xs,
    fontWeight: 500,
    color: colors.textMuted,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${colors.border}`,
    background: colors.background,
  }

  const isEmpty = subfolders.length === 0 && templates.length === 0

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDragging(null)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
        <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, margin: 0, letterSpacing: tracking.tight }}>
          Templates
        </h1>
        {!readOnly && <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.sm }}>
          <Button
            variant="secondary"
            icon={<FontAwesomeIcon icon={faFolderPlus} />}
            onClick={() => { setNewFolderName(''); setNewFolderOpen(true) }}
            disabled={atDepthLimit}
            title={atDepthLimit ? `Maximum folder depth of ${MAX_FOLDER_DEPTH} levels reached.` : undefined}
          >
            New Folder
          </Button>
          <Button
            variant="secondary"
            icon={<FontAwesomeIcon icon={faFileCirclePlus} />}
            onClick={() => navigate(`/app/templates/new-text?start=blank${folderId ? `&folderId=${folderId}` : ''}`)}
          >
            New Template
          </Button>
          <Button
            variant="secondary"
            icon={<FontAwesomeIcon icon={faTableCellsLarge} />}
            onClick={() => navigate(`/app/templates/new-text?start=layout${folderId ? `&folderId=${folderId}` : ''}`)}
          >
            Start from a layout
          </Button>
          <Button
            variant="primary"
            icon={<FontAwesomeIcon icon={faUpload} />}
            onClick={() => setShowUpload(true)}
          >
            Upload Template(s)
          </Button>
        </div>}
      </div>

      {readOnly && <LiveReadOnlyBanner />}

      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`,
          borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{error}</div>
      )}
      {notice && (
        <div style={{
          background: colors.successLight, color: colors.success,
          border: `1px solid ${colors.successBorder}`,
          borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{notice}</div>
      )}

      <div style={{ display: 'flex', gap: spacing.md, alignItems: 'flex-start' }}>
        {/* Folder tree */}
        <div style={{
          width: 280, flexShrink: 0,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.md,
          boxShadow: shadow.sm,
          padding: spacing.sm,
          maxHeight: '70vh',
          overflow: 'auto',
        }}>
          <FolderTree
            tree={tree}
            selectedFolderId={folderId}
            expanded={expanded}
            templatesByFolder={templatesByFolder}
            onToggle={toggle}
            onSelect={selectFolder}
            onOpenTemplate={id => navigate(`/app/templates/${id}`)}
            draggingFolderPath={dragging?.kind === 'folder' ? dragging.path : undefined}
          />
        </div>

        {/* Contents */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: spacing.sm, flexWrap: 'wrap' }}>
            <BreadcrumbCrumb
              folderId={null}
              label="Templates"
              isLast={crumbs.length === 0}
              onClick={() => selectFolder(null)}
              disabled={dragging === null}
            />
            {crumbs.map((crumb, i) => (
              <span key={crumb.folderId} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 8, color: colors.textMuted }} />
                <BreadcrumbCrumb
                  folderId={crumb.folderId}
                  label={crumb.name}
                  isLast={i === crumbs.length - 1}
                  onClick={() => selectFolder(crumb.folderId)}
                  disabled={
                    dragging === null ||
                    (dragging.kind === 'folder' && !!dragging.path && crumb.path.startsWith(dragging.path))
                  }
                />
              </span>
            ))}
          </div>

          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            boxShadow: shadow.sm,
            overflow: 'hidden',
          }}>
            {loading ? (
              <div style={{ padding: spacing.xl, color: colors.textSecondary }}>Loading...</div>
            ) : isEmpty ? (
              <div style={{ padding: spacing.xxl, textAlign: 'center', color: colors.textMuted }}>
                <div style={{ fontSize: fontSize.lg, marginBottom: spacing.sm }}>
                  {currentFolder ? `"${currentFolder.name}" is empty` : readOnly ? 'No templates are live yet' : 'No templates yet'}
                </div>
                <div style={{ fontSize: fontSize.base }}>
                  {readOnly
                    ? 'Only templates with a version promoted to Live appear here. Promote a version in Test.'
                    : 'Upload a .docx template, or create a folder to organize what you have.'}
                </div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Name', 'Status', 'Description', 'Updated', ''].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {subfolders.map(f => (
                    <FolderContentRow
                      key={f.folderId}
                      folder={f}
                      onOpen={selectFolder}
                      onRename={folder => { setRenaming(folder); setRenameValue(folder.name) }}
                      onDelete={requestDelete}
                      onMove={folder => setMoveTarget({ kind: 'folder', folder })}
                      onApproval={isTeamAdmin ? setApprovalTarget : undefined}
                      draggingFolderPath={dragging?.kind === 'folder' ? dragging.path : undefined}
                      readOnly={readOnly}
                    />
                  ))}
                  {templates.map(t => (
                    <TemplateContentRow
                      key={t.templateId}
                      template={t}
                      onOpen={id => navigate(`/app/templates/${id}`)}
                      onRename={template => { setRenamingTemplate(template); setRenameValue(template.name) }}
                      onMove={template => setMoveTarget({ kind: 'template', template })}
                      readOnly={readOnly}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <DragOverlay>
        {dragging && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: spacing.xs,
            padding: `4px ${spacing.sm}`,
            background: colors.surface,
            border: `1px solid ${colors.primary}`,
            borderRadius: radius.sm,
            boxShadow: shadow.md,
            fontSize: fontSize.sm,
            color: colors.text,
          }}>
            <FontAwesomeIcon
              icon={dragging.kind === 'folder' ? faFolder : faFileLines}
              style={{ color: colors.textMuted, fontSize: 12 }}
            />
            {dragging.name}
          </div>
        )}
      </DragOverlay>

      {/* ── Modals ── */}

      {newFolderOpen && (
        <Modal
          open
          onClose={() => setNewFolderOpen(false)}
          title="New Folder"
          subtitle={currentFolder ? `Inside "${currentFolder.name}"` : 'At the top level'}
          width={420}
          footer={
            <>
              <Button variant="ghost" onClick={() => setNewFolderOpen(false)} disabled={busy}>Cancel</Button>
              <Button variant="primary" onClick={createFolder} loading={busy} disabled={!newFolderName.trim()}>
                Create
              </Button>
            </>
          }
        >
          <Input
            label="Folder name"
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') createFolder() }}
            autoFocus
          />
        </Modal>
      )}

      {renaming && (
        <Modal
          open
          onClose={() => setRenaming(null)}
          title="Rename Folder"
          width={420}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRenaming(null)} disabled={busy}>Cancel</Button>
              <Button variant="primary" onClick={renameFolder} loading={busy} disabled={!renameValue.trim()}>
                Save
              </Button>
            </>
          }
        >
          <Input
            label="Folder name"
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') renameFolder() }}
            autoFocus
          />
        </Modal>
      )}

      {renamingTemplate && (
        <Modal
          open
          onClose={() => setRenamingTemplate(null)}
          title="Rename Template"
          width={420}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRenamingTemplate(null)} disabled={busy}>Cancel</Button>
              <Button variant="primary" onClick={renameTemplate} loading={busy}
                disabled={!renameValue.trim() || renameValue.trim() === renamingTemplate.name}>
                Save
              </Button>
            </>
          }
        >
          <Input
            label="Template name"
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') renameTemplate() }}
            autoFocus
          />
        </Modal>
      )}

      {deleting && (
        <ConfirmModal
          open
          danger
          loading={busy}
          title={`Delete "${deleting.folder.name}"?`}
          confirmLabel="Delete folder and subfolders"
          message={
            <>
              This folder contains{' '}
              {deleting.counts?.descendantFolderCount ? `${deleting.counts.descendantFolderCount} subfolder${deleting.counts.descendantFolderCount === 1 ? '' : 's'}` : null}
              {deleting.counts?.descendantFolderCount && deleting.counts?.descendantTemplateCount ? ' and ' : null}
              {deleting.counts?.descendantTemplateCount ? `${deleting.counts.descendantTemplateCount} template${deleting.counts.descendantTemplateCount === 1 ? '' : 's'}` : null}
              .{' '}
              The subfolders will be deleted. The templates are <strong>not</strong> deleted — they move up to
              {' '}{deleting.folder.parentFolderId ? 'the parent folder' : 'the top level'}.
              {deleting.revokedKeys.length > 0 && (
                <ScopedKeyList
                  intro={`${deleting.revokedKeys.length === 1 ? 'This API key is' : 'These API keys are'} scoped to this folder or one inside it and will be revoked, in Live as well as Test:`}
                  keys={deleting.revokedKeys}
                />
              )}
            </>
          }
          onConfirm={confirmRecursiveDelete}
          onCancel={() => setDeleting(null)}
        />
      )}

      {scopeWarning && (
        <ConfirmModal
          open
          danger={scopeWarning.keys.length > 0}
          loading={busy}
          title={`Move "${scopeWarning.itemName}"?`}
          confirmLabel="Move anyway"
          message={
            <>
              {scopeWarning.keys.length > 0 && (
                <ScopedKeyList
                  intro={`Folder changes take effect in Live immediately. After this move, ${scopeWarning.keys.length === 1 ? 'this API key' : 'these API keys'} will be refused (403) for the templates that leave ${scopeWarning.keys.length === 1 ? 'its' : 'their'} folder:`}
                  keys={scopeWarning.keys}
                />
              )}
              {scopeWarning.approval && <ApprovalChangeList impact={scopeWarning.approval} />}
            </>
          }
          onConfirm={confirmScopeWarning}
          onCancel={() => setScopeWarning(null)}
        />
      )}

      {moveTarget && (
        <MoveToFolderModal
          itemName={moveTarget.kind === 'template' ? moveTarget.template.name : moveTarget.folder.name}
          folders={folders}
          currentFolderId={
            moveTarget.kind === 'template'
              ? moveTarget.template.folderId
              : moveTarget.folder.parentFolderId
          }
          movingFolderPath={moveTarget.kind === 'folder' ? moveTarget.folder.path : undefined}
          movingSubtreeMaxDepth={
            moveTarget.kind === 'folder'
              ? subtreeMaxDepth(nodesById.get(moveTarget.folder.folderId) ?? { folder: moveTarget.folder, children: [], depth: 1 })
              : undefined
          }
          maxDepth={MAX_FOLDER_DEPTH}
          loading={busy}
          onMove={performMove}
          onClose={() => setMoveTarget(null)}
        />
      )}

      {approvalTarget && (
        <FolderPromotionApprovalModal
          folder={approvalTarget}
          folders={folders}
          onClose={() => setApprovalTarget(null)}
          onSaved={message => { setNotice(message); load() }}
        />
      )}

      {showUpload && (
        <ImportTemplatesModal
          folderId={folderId ?? undefined}
          onClose={() => setShowUpload(false)}
          onSuccess={() => { setShowUpload(false); load() }}
        />
      )}
    </DndContext>
  )
}
